/**
 * Solution Menu Component
 *
 * Handles the solution hint input panel.
 * Migrated from the legacy MegoldasMenu constructor function in olaszmodra.js.
 */

/**
 * Configuration for SolutionMenu
 */
export interface SolutionMenuConfig {
    /** Initial solution text */
    initialSolution?: string;
    /** Check if solution input is allowed */
    isAllowed: () => boolean;
    /** Callback when solution changes */
    onSolutionChange?: (solution: string) => void;
}

/**
 * Solution Menu Component
 *
 * Manages the solution hint input area.
 */
export class SolutionMenu {
    private _solution: string;
    private readonly _config: SolutionMenuConfig;

    constructor(config: SolutionMenuConfig) {
        this._config = config;
        this._solution = config.initialSolution ?? '';
    }

    // ============================================================================
    // Getters/Setters
    // ============================================================================

    get solution(): string {
        return this._solution;
    }

    set solution(value: string) {
        this._solution = value;
        this._config.onSolutionChange?.(value);
    }

    /**
     * Set solution from external source
     */
    setSolution(value: string): void {
        this._solution = value;
    }

    /**
     * Get current solution
     */
    getSolution(): string {
        return this._solution;
    }

    // ============================================================================
    // Event Handlers
    // ============================================================================

    /**
     * Handle keyup event on solution textarea
     */
    handleKeyUp = (event: KeyboardEvent): void => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            const textarea = event.target as HTMLTextAreaElement;
            this.solution = textarea.value;
        }
    };

    /**
     * Handle blur event on solution textarea
     */
    handleBlur = (event: FocusEvent): void => {
        const textarea = event.target as HTMLTextAreaElement;
        this.solution = textarea.value;
    };

    // ============================================================================
    // Rendering
    // ============================================================================

    /**
     * Render the solution menu HTML
     */
    render(): string {
        if (!this._config.isAllowed()) {
            return '';
        }

        return `
      <form>
        <div class="input-block">
          <label for="textareaMegoldas">Megoldási segítség</label>
          <textarea
            name="textareaMegoldas"
            id="textareaMegoldas"
            rows="5"
          >${this._solution}</textarea>
        </div>
      </form>
    `.trim();
    }

    /**
     * Render to string (for legacy compatibility)
     */
    toString(): string {
        return this.render();
    }

    /**
     * Update the menu in the DOM
     */
    update(containerId = 'megoldasMenu'): void {
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = this.render();
            this.attachEvents();
        }
    }

    /**
     * Attach event handlers to the textarea
     */
    attachEvents(): void {
        const textarea = document.getElementById(
            'textareaMegoldas',
        ) as HTMLTextAreaElement;
        if (textarea) {
            textarea.addEventListener('keyup', this.handleKeyUp);
            textarea.addEventListener('blur', this.handleBlur);
        }
    }

    /**
     * Detach event handlers from the textarea
     */
    detachEvents(): void {
        const textarea = document.getElementById(
            'textareaMegoldas',
        ) as HTMLTextAreaElement;
        if (textarea) {
            textarea.removeEventListener('keyup', this.handleKeyUp);
            textarea.removeEventListener('blur', this.handleBlur);
        }
    }
}
